import { useState, useEffect } from 'react';
import { useUI } from '../contexts/UIContext';
import { auth } from '../firebase';

/**
 * "Mi Perfil" tab. Edits the user's public (name/city/bio) and private
 * (phone) profile fields. Saves via updateProfile which routes each
 * field to the correct Firestore collection (profiles vs profiles_private).
 */
export default function ProfileView({ user, profile, updateProfile, exportAllMyData, deleteMyAccount }) {
  const { showNotification, confirm } = useUI();
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [tempName, setTempName] = useState(profile.name || '');
  const [tempCity, setTempCity] = useState(profile.city || '');
  const [tempPhone, setTempPhone] = useState(profile.phone || '');
  const [tempBio, setTempBio] = useState(profile.bio || '');
  const [saving, setSaving] = useState(false);

  // Sync local form state when the profile snapshot updates externally
  useEffect(() => {
    setTempName(profile.name || '');
    setTempCity(profile.city || '');
    setTempPhone(profile.phone || '');
    setTempBio(profile.bio || '');
  }, [profile.name, profile.city, profile.phone, profile.bio]);

  const handleSave = async () => {
    if (!tempName.trim()) return;
    setSaving(true);
    try {
      await updateProfile({ name: tempName, city: tempCity, phone: tempPhone, bio: tempBio });
      showNotification('Perfil actualizado correctamente', 'success');
    } catch (err) {
      // updateProfile already reports quota/permission errors to the
      // global banner + Sentry via handleFirestoreWrite. We just need
      // to tell THIS user that the action they just tried didn't go
      // through, so they don't think their changes were saved.
      console.error('Profile save failed:', err);
      showNotification('No pudimos guardar tu perfil. Inténtalo de nuevo.', 'error');
    } finally {
      // Crucially in finally — without this the button stays stuck
      // on "Guardando..." forever when the write fails.
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: '600px' }}>
      <div className="section-header">
        <h2>👤 Mi Perfil</h2>
        <p className="subtitle">Configura cómo te ven tus amigos cuando te agregan</p>
      </div>
      <div className="stat-card" style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--accent-gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', color: 'white', fontWeight: 700 }}>
            {tempName ? tempName.charAt(0).toUpperCase() : '?'}
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 15 }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--accent-gold)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              🌐 Información Pública
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>Nombre</label>
              <input
                type="text"
                className="input-field"
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                placeholder="Tu nombre o apodo..."
              />
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>Ciudad / Ubicación</label>
              <input
                type="text"
                className="input-field"
                value={tempCity}
                onChange={(e) => setTempCity(e.target.value)}
                placeholder="Ej. Bogotá, Madrid, CDMX..."
              />
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>Sobre mí / Bio</label>
              <textarea
                className="btn"
                style={{ width: '100%', padding: '12px', fontSize: '0.9rem', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'white', textAlign: 'left', minHeight: '80px', resize: 'vertical' }}
                value={tempBio}
                onChange={(e) => setTempBio(e.target.value)}
                placeholder="Ej. Cambio en persona los fines de semana..."
              />
            </div>

            <div style={{ height: 1, background: 'var(--border-color)', margin: '8px 0' }} />

            <div style={{ fontSize: '0.75rem', color: '#4ade80', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              🔒 Información Privada
            </div>
            <div>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>WhatsApp / Teléfono (Opcional)</label>
              <input
                type="text"
                className="input-field"
                value={tempPhone}
                onChange={(e) => setTempPhone(e.target.value)}
                placeholder="Ej. +57 300..."
              />
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.4 }}>
                🔒 Tu teléfono <strong>no se muestra a nadie</strong> en la app — ni siquiera a quienes ven tu álbum compartido. Lo guardamos solo para ti.
              </p>
            </div>
          </div>
        </div>
        <div style={{ padding: '12px 14px', background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 10, fontSize: '0.83rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          ℹ️ <strong>Privacidad:</strong> Cuando alguien abre tu link público solo ve tu nombre, ciudad, bio y láminas. Tu teléfono nunca se expone.
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-gold" style={{ padding: '12px 24px' }} onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>

      <div style={{ marginTop: '30px', padding: '20px', borderRadius: '12px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>ID de Usuario</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <code style={{ background: 'var(--bg-card)', padding: '6px 12px', borderRadius: '6px', fontSize: '0.9rem', flex: 1 }}>{user.uid}</code>
          <button className="btn" onClick={() => { navigator.clipboard.writeText(user.uid); showNotification('ID copiado', 'success'); }}>📋</button>
        </div>
      </div>

      {/* Privacy & data rights — GDPR / LGPD compliance */}
      <div style={{ marginTop: '24px', padding: '20px', borderRadius: '12px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-color)' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: 6 }}>🔐 Privacidad y datos</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
          Tus datos te pertenecen. Puedes descargar una copia completa o eliminar tu cuenta en cualquier momento.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            className="btn"
            onClick={async () => {
              if (exportAllMyData) await exportAllMyData();
            }}
          >
            📥 Descargar mis datos
          </button>
          <button
            className="btn"
            style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.4)' }}
            onClick={() => { setShowDeleteDialog(true); setConfirmText(''); }}
          >
            🗑️ Eliminar mi cuenta
          </button>
        </div>
      </div>

      {/* Delete account confirm dialog */}
      {showDeleteDialog && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => !deleting && setShowDeleteDialog(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)', padding: 26, borderRadius: 16,
              maxWidth: 460, width: '100%',
              border: '1px solid rgba(239,68,68,0.4)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 12, color: '#f87171' }}>
              ⚠️ Esto no se puede deshacer
            </h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.5 }}>
              Vamos a borrar permanentemente:
            </p>
            <ul style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 16, paddingLeft: 22 }}>
              <li>Tu álbum completo (láminas marcadas, repetidas, historial)</li>
              <li>Tu perfil público (nombre, ciudad, bio, foto)</li>
              <li>Tu teléfono y token de notificaciones</li>
              <li>Todas las propuestas de intercambio que enviaste y recibiste</li>
            </ul>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 14 }}>
              Tip: descarga tus datos primero si quieres conservarlos.
            </p>
            <label style={{ fontSize: '0.85rem', color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>
              Para confirmar, escribe <strong style={{ color: '#f87171' }}>ELIMINAR</strong>:
            </label>
            <input
              type="text"
              className="input-field"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ELIMINAR"
              disabled={deleting}
              style={{ marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
                Cancelar
              </button>
              <button
                className="btn"
                style={{
                  background: confirmText === 'ELIMINAR' ? '#ef4444' : 'rgba(239,68,68,0.2)',
                  color: confirmText === 'ELIMINAR' ? 'white' : 'var(--text-muted)',
                  cursor: confirmText === 'ELIMINAR' ? 'pointer' : 'not-allowed',
                  border: 'none', fontWeight: 700,
                }}
                disabled={confirmText !== 'ELIMINAR' || deleting}
                onClick={async () => {
                  if (confirmText !== 'ELIMINAR') return;
                  setDeleting(true);
                  try {
                    // 1. Delete all Firestore data
                    await deleteMyAccount();
                    // 2. Delete the Firebase Auth user (signs them out)
                    try {
                      await auth.currentUser?.delete();
                    } catch (err) {
                      // Auth deletion may need recent re-auth; if it fails,
                      // we still removed all data from Firestore. The user
                      // can sign out manually.
                      console.warn('Auth delete failed (likely needs reauth):', err);
                    }
                    // 3. Clear any local cache
                    try { localStorage.clear(); } catch {}
                    // 4. Hard reload to a clean state
                    window.location.href = '/';
                  } catch (err) {
                    console.error('Account deletion failed:', err);
                    showNotification('No pudimos eliminar tu cuenta. Inténtalo de nuevo.', 'error');
                    setDeleting(false);
                  }
                }}
              >
                {deleting ? 'Eliminando…' : 'Eliminar todo'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
